/**
 * BrandBreakdown.jsx
 * Performance by brand, and within each brand by platform.
 *
 * The headline figures on the dashboard add everything together, which is
 * the right answer to "how did it go" and the wrong one to "for whom, and
 * where". This card keeps them apart: each brand gets a block, each platform
 * a row, and each row says what it published, how many people saw it, what
 * they did, how that compares with the previous window, and which post did
 * best.
 *
 * Facebook and Instagram are never added together inside a brand's row. They
 * report different things under the same names — Facebook has link clicks and
 * no saves, Instagram the reverse — so a combined figure would be comparing
 * unlike things. The brand's own line sums only what both platforms share.
 *
 * Unmeasured stays "—", never 0. And where Meta reports a genuine zero for
 * posts it has measured, the row says so in words, because 21 posts with no
 * views is a finding worth reading, not a number to skim past.
 *
 * Dependencies: helpers.js, services/api.js, components/Icons.jsx
 */

import { api } from '../../services/api.js';
import { Image } from '../Icons.jsx';
import InstagramHealth from './InstagramHealth.jsx';
import { PLATFORMS, change, compact, direction, full, percent, snippet } from './helpers.js';

export default function BrandBreakdown({ rows = [], days, loading }) {
  // Everyone's share is measured against the whole, so the bars compare
  // brands and platforms with each other rather than each with itself.
  const totalEngagements = rows.reduce((n, r) => n + (r.engagements ?? 0), 0);
  const totalImpressions = rows.reduce((n, r) => n + (r.impressions ?? 0), 0);

  const brands = groupByBrand(rows);

  return (
    <section className="card brand-breakdown">
      <div className="card__head">
        <div className="card__head-text">
          <h2>Performance by brand and platform</h2>
          <p>
            Posts published in the last {days} days, split by brand and by Facebook or Instagram.
            Compared with the {days} days before.
          </p>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <p className="mono">Loading…</p>
      ) : brands.length === 0 ? (
        <p className="hint">No brands with a connected Facebook or Instagram account yet.</p>
      ) : (
        brands.map((brand) => (
          <BrandBlock
            key={brand.id}
            brand={brand}
            totalEngagements={totalEngagements}
            totalImpressions={totalImpressions}
          />
        ))
      )}

      <p className="hint" style={{ marginTop: 10 }}>
        Engagements are likes (or reactions), comments, shares and saves. Facebook does not report
        saves and Instagram does not report link clicks on ordinary posts, so those show as —.
      </p>

      {/* When a row says Meta reports no views, this is how to find out why. */}
      <InstagramHealth />
    </section>
  );
}

/** One brand: its own totals, then a row for each platform. */
function BrandBlock({ brand, totalEngagements, totalImpressions }) {
  const sum = (key) => {
    const measured = brand.rows.filter((r) => r[key] !== null && r[key] !== undefined);
    return measured.length ? measured.reduce((n, r) => n + r[key], 0) : null;
  };

  const published = brand.rows.reduce((n, r) => n + (r.published ?? 0), 0);
  const impressions = sum('impressions');
  const engagements = sum('engagements');
  const rate = impressions ? (engagements / impressions) * 100 : null;
  const share = totalEngagements > 0 && engagements !== null ? (engagements / totalEngagements) * 100 : null;
  const seenShare =
    totalImpressions > 0 && impressions !== null ? (impressions / totalImpressions) * 100 : null;

  return (
    <div className="bb-brand">
      <div className="bb-brand__head">
        <span className="bb-brand__avatar">{(brand.name ?? '?').slice(0, 1)}</span>
        <div className="bb-brand__name">{brand.name}</div>
        <div className="bb-brand__sum">
          <span>
            <strong>{full(published)}</strong> posts
          </span>
          <span>
            <strong>{compact(impressions)}</strong> seen
            {seenShare !== null && <em> · {percent(seenShare, 0)} of all views</em>}
          </span>
          <span>
            <strong>{compact(engagements)}</strong> engagements
            {share !== null && <em> · {percent(share, 0)} of all engagement</em>}
          </span>
          <span>
            <strong>{percent(rate)}</strong> rate
          </span>
        </div>
      </div>

      <div className="bb-table" role="table" aria-label={`${brand.name} by platform`}>
        <div className="bb-row bb-row--head" role="row">
          <span role="columnheader">Platform</span>
          <span role="columnheader">Posts</span>
          <span role="columnheader">Seen</span>
          <span role="columnheader">Reach</span>
          <span role="columnheader">Engagements</span>
          <span role="columnheader">Rate</span>
          <span role="columnheader">What people did</span>
          <span role="columnheader">Best post</span>
        </div>

        {brand.rows.map((row) => (
          <PlatformRow key={row.platform} row={row} totalEngagements={totalEngagements} />
        ))}
      </div>
    </div>
  );
}

/** One platform within a brand. */
function PlatformRow({ row, totalEngagements }) {
  const platform = PLATFORMS[row.platform] ?? { label: row.platform, Icon: null, className: '' };
  const { Icon: Mark } = platform;

  const seenDelta = change(row.impressions, row.previous?.impressions);
  const engDelta = change(row.engagements, row.previous?.engagements);
  const share =
    totalEngagements > 0 && row.engagements !== null ? (row.engagements / totalEngagements) * 100 : 0;

  // Posts went out and Meta measured them, but reports nobody saw them. Said in
  // words, because it is the most important thing on the row when it happens.
  const measuredNothing = row.measured > 0 && row.impressions === 0;

  return (
    <div className="bb-row" role="row">
      <span className="bb-platform" role="cell">
        <span className={`platform-mark ${platform.className}`}>{Mark && <Mark size={13} />}</span>
        {platform.label}
      </span>

      <span role="cell" data-label="Posts">
        <strong>{full(row.published)}</strong>
        {row.published > 0 && row.measured < row.published && (
          <small> ({row.measured} measured)</small>
        )}
        <Delta current={row.published} previous={row.previous?.published} />
      </span>

      <span role="cell" data-label="Seen">
        <strong>{compact(row.impressions)}</strong>
        <Delta value={seenDelta} />
      </span>

      <span role="cell" data-label="Reach">
        <strong>{compact(row.reach)}</strong>
      </span>

      <span role="cell" data-label="Engagements">
        <strong>{compact(row.engagements)}</strong>
        <Delta value={engDelta} />
        <span className="bb-bar" aria-hidden="true">
          <span className={`bb-bar__fill ${platform.className}`} style={{ width: `${share}%` }} />
        </span>
      </span>

      <span role="cell" data-label="Rate">
        <strong>{percent(row.rate)}</strong>
      </span>

      <span role="cell" data-label="What people did" className="bb-mix">
        <Mix label="likes" value={row.likes} />
        <Mix label="comments" value={row.comments} />
        <Mix label="shares" value={row.shares} />
        <Mix label="saves" value={row.saves} />
        <Mix label="clicks" value={row.clicks} />
      </span>

      <span role="cell" data-label="Best post">
        <BestPost post={row.best} />
      </span>

      {row.published === 0 && (
        <span className="bb-note" role="note">
          Nothing published to {platform.label} in this window.
        </span>
      )}
      {measuredNothing && (
        <span className="bb-note bb-note--warn" role="note">
          Meta reports no views for these {row.published} posts — it measured them and returned zero,
          with no error. That is Meta&rsquo;s figure, not a gap in Kreeboom&rsquo;s collection.
          Use <strong>Check Instagram accounts with Meta</strong> below to see why.
        </span>
      )}
    </div>
  );
}

/** Up or down against the previous window, or nothing where that would be a lie. */
function Delta({ value, current, previous }) {
  const d = value !== undefined ? value : change(current, previous);
  if (d === null || d === undefined) return null;
  const dir = direction(d);
  const sign = d > 0 ? '+' : '';
  return <small className={`bb-delta is-${dir}`}>{`${sign}${d.toFixed(0)}%`}</small>;
}

const Mix = ({ label, value }) => (
  <span className="bb-mix__item">
    <strong>{value === null || value === undefined ? '—' : compact(value)}</strong> {label}
  </span>
);

/** The post that earned the most, with a way to go and look at it. */
function BestPost({ post }) {
  if (!post) return <span className="hint">—</span>;

  return (
    <span className="bb-best">
      {post.mediaId ? (
        <img
          className="bb-best__thumb"
          src={api.media.rawUrl(post.mediaId)}
          alt=""
          loading="lazy"
        />
      ) : (
        <span className="bb-best__thumb bb-best__thumb--empty">
          <Image size={14} />
        </span>
      )}
      <span className="bb-best__text">
        <span className="bb-best__caption">{snippet(post.caption, 60) || 'No caption'}</span>
        <small>
          {compact(post.engagements)} engagements · {compact(post.impressions)} seen
          {post.permalink && (
            <>
              {' · '}
              <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                open
              </a>
            </>
          )}
        </small>
      </span>
    </span>
  );
}

/** Rows grouped under their brand, in the order the server sent them. */
function groupByBrand(rows) {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.brandId)) map.set(r.brandId, { id: r.brandId, name: r.brandName, rows: [] });
    map.get(r.brandId).rows.push(r);
  }
  return [...map.values()];
}
